// src/pages/Billing.jsx
import React, { useState } from "react";
import { Check } from "lucide-react";
import Navbar from "../components/Navbar";
import Sidebar from "../components/Sidebar";
import Toast from "../components/Toast";
import { pricingPlans } from "../data/mockData";
import CheckoutButton from "../components/CheckoutButton";

const Billing = ({ darkMode, toggleDarkMode }) => {
  const [toast, setToast] = useState(null);

  // show toast helper
  const showToast = (message, type = "info") => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 5000);
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      <Navbar darkMode={darkMode} toggleDarkMode={toggleDarkMode} />

      <div className="flex">
        <Sidebar />

        <main className="flex-1 p-8">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
              Billing & Subscription
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              Choose the plan that works best for you
            </p>
          </div>

          {/* Current Plan Banner */}
          <div className="card mb-8 bg-gradient-to-r from-primary-50 to-primary-100 dark:from-primary-900 dark:to-primary-800 border-2 border-primary-300 dark:border-primary-700">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-primary-900 dark:text-primary-100 mb-1">
                  Current Plan: Free
                </h3>
                <p className="text-primary-700 dark:text-primary-300">
                  5 papers per month • Basic features
                </p>
              </div>
              <div className="text-right">
                <p className="text-sm text-primary-700 dark:text-primary-300 mb-1">
                  Papers used this month
                </p>
                <div className="flex items-center space-x-2">
                  <div className="w-32 bg-primary-200 dark:bg-primary-700 rounded-full h-2">
                    <div
                      className="bg-primary-600 h-2 rounded-full"
                      style={{ width: "60%" }}
                    />
                  </div>
                  <span className="text-primary-900 dark:text-primary-100 font-semibold">
                    3/5
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Pricing Plans */}
          <div className="grid md:grid-cols-3 gap-8">
            {pricingPlans.map((plan, index) => (
              <div
                key={index}
                className={`card relative ${plan.popular ? "border-2 border-primary-600 shadow-xl scale-105" : ""}`}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-primary-600 text-white px-4 py-1 rounded-full text-sm font-semibold">
                      Most Popular
                    </span>
                  </div>
                )}

                <div className="text-center mb-6">
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                    {plan.name}
                  </h3>
                  <div className="flex items-baseline justify-center mb-2">
                    <span className="text-4xl font-bold text-gray-900 dark:text-white">
                      {plan.price}
                    </span>
                    <span className="text-gray-600 dark:text-gray-400 ml-2">
                      /{plan.period}
                    </span>
                  </div>
                </div>

                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start space-x-3">
                      <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700 dark:text-gray-300">{feature}</span>
                    </li>
                  ))}
                </ul>

                {/* Action area */}
                <div>
                  {/* Free plan -> show disabled "Current plan" */}
                  {plan.name === "Free" ? (
                    <button
                      disabled
                      className="w-full py-3 px-4 rounded-lg font-medium bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white"
                    >
                      Current Plan
                    </button>
                  ) : (
                    // If the plan has a priceId (backend-enabled), show CheckoutButton; otherwise fallback to contact sales
                    plan.priceId ? (
                      <CheckoutButton
                        priceId={plan.priceId}
                        onStart={() => showToast(`Redirecting to checkout for ${plan.name}...`, "info")}
                        onError={(msg) => showToast(`Checkout failed: ${msg}`, "error")}
                      >
                        {plan.buttonText || "Buy now"}
                      </CheckoutButton>
                    ) : (
                      <button
                        className="w-full py-3 px-4 rounded-lg font-medium bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-900 dark:text-white"
                        onClick={() => showToast("Please contact sales for Team plan.", "info")}
                      >
                        {plan.buttonText || "Contact Sales"}
                      </button>
                    )
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* FAQ Section */}
          <div className="mt-12 card">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
              Frequently Asked Questions
            </h2>
            <div className="space-y-6">
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white mb-2">
                  Can I change my plan anytime?
                </h3>
                <p className="text-gray-600 dark:text-gray-400">
                  Yes! You can upgrade or downgrade your plan at any time. Changes will be reflected in your next billing cycle.
                </p>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white mb-2">
                  What payment methods do you accept?
                </h3>
                <p className="text-gray-600 dark:text-gray-400">
                  We accept all major credit cards and Stripe payments.
                </p>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white mb-2">
                  Is there a free trial?
                </h3>
                <p className="text-gray-600 dark:text-gray-400">
                  Yes! All new users start with our Free plan. You can try Pro features with a 14-day free trial when you upgrade.
                </p>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white mb-2">
                  What happens if I exceed my quota?
                </h3>
                <p className="text-gray-600 dark:text-gray-400">
                  You'll need to upgrade to continue analyzing papers. We'll notify you when you're close to your limit.
                </p>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default Billing;